import { authorizedFetch } from '@/utils/apiClient';

// A coach's own DoorDash account (DOORDASH_CONNECT_PLAN.md, step 3). The server
// decides who sees any of this (DOORDASH_USER_ACCOUNTS + its allowlist), so the
// UI never needs its own flag: `enabled: false` means show nothing.

async function readJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export const DISCONNECTED = { enabled: false, connected: false, status: null };

export async function getDoordashConnection() {
  const response = await authorizedFetch('/api/doordash/connection');
  if (!response.ok) return DISCONNECTED;
  const body = await readJson(response);
  return body && typeof body === 'object' ? { ...DISCONNECTED, ...body } : DISCONNECTED;
}

/**
 * Starts DoorDash sign-in; the browser leaves for DoorDash on success.
 * `forceLogin` asks DoorDash for its login screen even when this browser is
 * already signed in, to connect a different DoorDash account.
 */
export async function startDoordashConnect(returnTo = window.location.pathname, { forceLogin = false } = {}) {
  // Deliberately on this page's own origin, not VITE_API_BASE_URL: the deployed
  // site calls Railway directly, but DoorDash returns to <site>/api/doordash/
  // oauth/callback through the site's /api proxy. The binding cookie must be set
  // on the site's host or the callback never sees it. buildApiUrl passes an
  // absolute URL through untouched.
  const response = await authorizedFetch(
    `${window.location.origin}/api/doordash/oauth/start?returnTo=${encodeURIComponent(returnTo)}${forceLogin ? '&prompt=login' : ''}`,
    { method: 'POST' }
  );
  const body = await readJson(response);
  if (!response.ok || !body?.authorizeUrl) {
    throw new Error(body?.error || 'Could not start DoorDash sign-in. Please try again.');
  }
  window.location.assign(body.authorizeUrl);
}

const cardsFrom = (body) => {
  const label = typeof body?.card?.label === 'string' ? body.card.label.trim() : '';
  return {
    card: label ? { id: body.card.id ?? null, label, expired: body.card.expired === true } : null,
    cards: Array.isArray(body?.cards) ? body.cards : [],
    available: body?.available === true,
  };
};

/**
 * The cards on this coach's DoorDash account: { card, cards, available } —
 * `card` is the default (or null), `cards` all of them ({ id, label,
 * isDefault, expired }), `available` false when they could not be read.
 * `id` is DoorDash's opaque card id, for picking a card for one order
 * (providerCardId on a quote/submit); never saved with an order.
 */
export async function getDoordashPaymentCards() {
  const unavailable = { card: null, cards: [], available: false };
  try {
    const response = await authorizedFetch('/api/doordash/payment-card');
    if (!response.ok) return unavailable;
    return cardsFrom(await readJson(response));
  } catch {
    return unavailable;
  }
}

/** Make a saved card the DoorDash default (in the DoorDash app too). Answers with the cards as they now read. */
export async function setDoordashDefaultCard(cardId) {
  const response = await authorizedFetch('/api/doordash/payment-card/default', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cardId }),
  });
  const body = await readJson(response);
  if (!response.ok) throw new Error(body?.error || 'Couldn’t change your DoorDash card right now. Try again in a minute.');
  return cardsFrom(body);
}

export async function disconnectDoordash() {
  const response = await authorizedFetch('/api/doordash/connection', { method: 'DELETE' });
  if (!response.ok) {
    const body = await readJson(response);
    throw new Error(body?.error || 'Could not disconnect DoorDash. Please try again.');
  }
  return DISCONNECTED;
}
